import styled from '@emotion/styled';
import React, { forwardRef } from 'react';
import { segment } from '@actions/analytics';
import {
  PLANNER_VIDEO_CARD_CLICKED,
  PLANNER_PAGE,
  PLANNER_VIDEO_CARD_VIEWED
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import Image from '@base/FutureImage';
import { Spacings, Fonts } from '@styles/index';

import MUIDialog from '@material-ui/core/Dialog';
import Slide from '@material-ui/core/Slide';
import Youtube from '@cont/FreeLesson/externalyoutube';
import pushRoute from '@utils/pushRoute';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import Button from 'aquilla/core/molecules/Button';
import Typography from 'aquilla/core/atoms/Typography';
import {
  browseTopSpacing,
  dayTitleLeftSpacing,
  plannerItemTypeHeightMap
} from '../planner-constants';

const OuterWrapper = styled.div`
  padding-top: ${browseTopSpacing}px;
  height: ${({ $height }) => $height}px;
`;

const Wrapper = styled.div`
  margin-left: ${dayTitleLeftSpacing};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  background-image: ${({ $imageURL }) => $imageURL && `url(${$imageURL})`};
  background-repeat: no-repeat;
  background-position: bottom right;
  background-size: cover;
  border-radius: ${Spacings.SPACING_2B};
  padding: ${({ $paddingTop }) => `${$paddingTop} ${Spacings.SPACING_8B}`};
  height: 100%;
  position: relative;
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacings.SPACING_1B};
`;

const Header = styled(Typography)`
  font-size: ${Fonts.ULTRA_SMALL};
  font-weight: ${Fonts.HEAVY};
`;

const Title = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_6};
  max-width: 268px;
`;

const Description = styled(Typography)`
  max-width: 268px;
`;

const StyledButton = styled(Button)`
  position: absolute;
  bottom: ${({ $bottom }) => $bottom};
`;

const opts = {
  height: '100%',
  width: '100%',
  playerVars: {
    autoplay: 1,
    showinfo: 0,
    rel: 0
  }
};

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(MUIDialog)`
  &.MuiDialog-root .MuiDialog-container .MuiPaper-root {
    ${({ $center }) => !$center && 'position: absolute;'}
    ${({ $center }) => !$center && 'bottom: 0px;'}
    background: var(--color-text-primary);
    height: ${({ $height }) => $height};
    width: ${({ $width }) => $width};
    max-width: ${({ $width }) => $width};
    @media only screen and (max-width: 540px) {
      margin: 16px auto;
    }
  }
`;

const calcVideoId = (url) => {
  if (!url) return null;

  const videoid = url.match(
    /(?:https?:\/{2})?(?:w{3}\.)?youtu(?:be)?\.(?:com|be)(?:\/watch\?v=|\/)([^\s&]+)/
  );
  if (videoid != null) {
    return videoid[1];
  }
  return null;
};

const FreeTrialCard = ({ metaItem }) => {
  const { plannerItemType, itemInfo } = metaItem;
  const height = plannerItemTypeHeightMap[plannerItemType];

  const { item_info, object_uid, description, title, header_text } =
    itemInfo || {};

  const {
    web_deeplink,
    web_video_url,
    image_url,
    cta_text,
    card_css_property
  } = item_info || {};

  const {
    background_color,
    text_color,
    header_color,
    title_color,
    description_color,
    button_variant
  } = card_css_property || {};
  const videoId = calcVideoId(web_video_url);
  const goalUID = useSelectedGoalUID();
  const goalSegmentData = useGoalEventProps({ goalUID });

  React.useEffect(() => {
    segment.track(PLANNER_VIDEO_CARD_VIEWED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      video_title: title,
      video_uid: object_uid
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [openVideo, setOpenVideo] = React.useState(false);
  const closeVideo = () => {
    setOpenVideo(false);
  };

  const handleClick = () => {
    segment.track(PLANNER_VIDEO_CARD_CLICKED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      video_title: title,
      video_uid: object_uid
    });

    if (web_video_url) {
      setOpenVideo(true);
    } else if (web_deeplink) {
      pushRoute(web_deeplink, web_deeplink);
    }
  };

  const showVideoDialog = videoId && openVideo;
  return (
    <>
      <OuterWrapper $height={height}>
        <Wrapper
          $backgroundColor={background_color}
          $imageURL={image_url}
          $paddingTop={header_text ? Spacings.SPACING_6B : Spacings.SPACING_7B}
        >
          {!!header_text && (
            <HeaderWrapper>
              <Header variant="h4" color={header_color || text_color}>
                {header_text}
              </Header>
              <Image
                src="planner/feature_star.svg"
                width={12}
                height={12}
                alt="feature"
              />
            </HeaderWrapper>
          )}
          <Title variant="h4" color={title_color || text_color}>
            {title}
          </Title>
          <Description variant="p2" color={description_color || text_color}>
            {description}
          </Description>
          <StyledButton
            variant={button_variant || 'primary'}
            label={cta_text}
            size="small"
            onClick={handleClick}
            $bottom={header_text ? Spacings.SPACING_6B : Spacings.SPACING_7B}
          />
        </Wrapper>
      </OuterWrapper>
      <StyledDialog
        open={showVideoDialog}
        TransitionComponent={Transition}
        onClose={closeVideo}
        $center={false}
        $height="80%"
        $width="88%"
      >
        <Youtube
          opts={opts}
          videoId={videoId}
          onEnd={closeVideo}
          useWrapper={false}
        />
      </StyledDialog>
    </>
  );
};

export default FreeTrialCard;
